/**
 * #524 — Rewards program value settings.
 *
 * The admin surface for the SINGLE source of truth behind point valuation:
 * how many cents a redeemed point is worth, and how many points a dollar of
 * spend earns. Both the customer checkout redemption leg and the desk
 * redemption tiers read this same value, so editing it here moves every
 * surface at once — killing the historical 1¢/10¢ contradiction (#524).
 *
 * Design Philosophy principles served:
 *  - #1 One glance, one truth: the effective "100 points = $X.XX" is the
 *    hero; the tiers below it derive from the SAME server-computed value, so
 *    no two numbers on screen can disagree.
 *  - #2 The next action is obvious: one primary "Save value" button; a
 *    non-owner sees a plain reason instead of a dead control.
 *  - #5 Never lie about data: loading renders a skeleton (not a spinner
 *    wall); a failed load is an honest error with Retry, distinct from a
 *    real value; the default (1¢) is labelled as the platform default so it
 *    reads differently from a deliberately-set value.
 *  - Five states: loading (skeleton) · error (+retry) · ready (default) ·
 *    saving · success. All present.
 */

import { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Input } from '@ferro/ui';
import {
  adminLoyaltyConfigApi,
  type LoyaltyConfigResponse,
} from '../lib/api.js';

interface LoyaltySettingsCardProps {
  apiUrl: string;
  /** Owner-tier (business-config.pricing:write:all). Non-owners see read-only. */
  canEdit: boolean;
  /**
   * Fired with the effective cents-per-point whenever it loads or changes,
   * so the parent Loyalty page derives its redemption tiers from the SAME
   * value (one source of truth). Optional.
   */
  onValueChanged?: (redemptionValueCentsPerPoint: number) => void;
}

type Status = 'loading' | 'error' | 'ready';

function dollars(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

export function LoyaltySettingsCard({
  apiUrl,
  canEdit,
  onValueChanged,
}: LoyaltySettingsCardProps): JSX.Element {
  const [status, setStatus] = useState<Status>('loading');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [config, setConfig] = useState<LoyaltyConfigResponse | null>(null);

  // Edit form (owner only)
  const [valueCents, setValueCents] = useState('');
  const [earnRate, setEarnRate] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const applyConfig = useCallback(
    (c: LoyaltyConfigResponse) => {
      setConfig(c);
      setValueCents(String(c.redemption_value_cents_per_point));
      setEarnRate(String(c.earn_rate_points_per_dollar));
      onValueChanged?.(c.redemption_value_cents_per_point);
    },
    [onValueChanged],
  );

  const load = useCallback(async () => {
    setStatus('loading');
    setLoadError(null);
    const res = await adminLoyaltyConfigApi.get(apiUrl);
    if (res.data) {
      applyConfig(res.data);
      setStatus('ready');
    } else {
      setLoadError(res.error ?? 'Could not load rewards settings.');
      setStatus('error');
    }
  }, [apiUrl, applyConfig]);

  useEffect(() => {
    void load();
  }, [load]);

  // Live preview from the CURRENT form values (falls back to the loaded
  // config while typing), so the human meaning is always visible.
  const parsedCents = Number(valueCents);
  const previewCents = Number.isFinite(parsedCents) && parsedCents > 0
    ? Math.round(parsedCents)
    : config?.redemption_value_cents_per_point ?? 1;

  const clientValidationError = (): string | null => {
    const v = Number(valueCents);
    const r = Number(earnRate);
    if (!Number.isFinite(v) || !Number.isInteger(v) || v < 1) {
      return 'Point value must be a whole number of cents, at least 1.';
    }
    if (!Number.isFinite(r) || r <= 0) {
      return 'Earn rate must be a number of points greater than zero.';
    }
    return null;
  };

  const handleSave = useCallback(async () => {
    setSaveError(null);
    setSavedAt(null);
    const invalid = clientValidationError();
    if (invalid) {
      setSaveError(invalid);
      return;
    }
    setIsSaving(true);
    const res = await adminLoyaltyConfigApi.update(apiUrl, {
      redemptionValueCentsPerPoint: Math.round(Number(valueCents)),
      earnRatePointsPerDollar: Number(earnRate),
    });
    setIsSaving(false);
    if (res.data) {
      applyConfig(res.data);
      setSavedAt(Date.now());
    } else {
      setSaveError(res.error ?? 'Could not save. Please try again.');
    }
    // Reference savedAt so exhaustive-deps stays honest without re-running.
    void savedAt;
  }, [apiUrl, valueCents, earnRate, applyConfig, savedAt]);

  // ── Loading (skeleton, not a spinner wall) ──────────────────────────
  if (status === 'loading') {
    return (
      <Card title="Rewards program value">
        <div className="ferro-skeleton-stack" aria-busy="true" aria-live="polite">
          <div className="ferro-skeleton ferro-skeleton--line" style={{ width: '60%' }} />
          <div className="ferro-skeleton ferro-skeleton--line" style={{ width: '40%' }} />
          <div className="ferro-skeleton ferro-skeleton--line" style={{ width: '75%' }} />
        </div>
      </Card>
    );
  }

  // ── Error (honest, with recovery) ───────────────────────────────────
  if (status === 'error' || !config) {
    return (
      <Card title="Rewards program value">
        <Alert variant="error">
          {loadError ?? 'Could not load rewards settings.'}
        </Alert>
        <div style={{ marginTop: 'var(--ferro-space-3, 12px)' }}>
          <Button variant="outline" onClick={() => void load()}>Try again</Button>
        </div>
      </Card>
    );
  }

  // ── Ready ───────────────────────────────────────────────────────────
  const isDefault =
    config.redemption_value_cents_per_point === 1 &&
    config.earn_rate_points_per_dollar === 1;
  const example = config.example;

  return (
    <Card
      title="Rewards program value"
      subtitle="One setting drives both online redemption and the desk reward tiers."
    >
      {/* Hero: the effective value, one glance, one truth */}
      <div className="ferro-loyalty-preview" role="status">
        <div>
          <p className="ferro-loyalty-result__meta">Every point is worth</p>
          <p className="ferro-tier-btn__note">
            {config.earn_rate_points_per_dollar === 1
              ? 'Earn 1 point per $1 spent'
              : `Earn ${config.earn_rate_points_per_dollar} points per $1 spent`}
          </p>
        </div>
        <p className="ferro-loyalty-preview__value" style={{ fontVariantNumeric: 'tabular-nums' }}>
          {dollars(config.redemption_value_cents_per_point)}
        </p>
      </div>

      <p className="ferro-loyalty-result__meta" style={{ marginTop: 'var(--ferro-space-2, 8px)' }}>
        {example.points} points = <strong>{dollars(example.discount_cents)}</strong>
        {isDefault && ' — the platform default'}
      </p>

      {canEdit ? (
        <>
          <div className="ferro-form__row" style={{ marginTop: 'var(--ferro-space-4, 16px)' }}>
            <Input
              name="loyalty-point-value"
              label="Point value (¢ per point)"
              type="number"
              min="1"
              step="1"
              value={valueCents}
              onChange={(e) => setValueCents(e.target.value)}
            />
            <Input
              name="loyalty-earn-rate"
              label="Earn rate (points per $1)"
              type="number"
              min="0"
              step="0.1"
              value={earnRate}
              onChange={(e) => setEarnRate(e.target.value)}
            />
          </div>

          <p className="ferro-loyalty-result__meta">
            Preview: 100 points ={' '}
            <strong>{dollars(100 * previewCents)}</strong>
          </p>

          {saveError && <Alert variant="error">{saveError}</Alert>}
          {savedAt && !saveError && (
            <Alert variant="success">
              Saved. Every point is now worth {dollars(config.redemption_value_cents_per_point)}.
            </Alert>
          )}

          <div style={{ marginTop: 'var(--ferro-space-3, 12px)' }}>
            <Button onClick={() => void handleSave()} isLoading={isSaving}>
              Save value
            </Button>
          </div>
        </>
      ) : (
        <p
          className="ferro-tier-btn__note"
          style={{ marginTop: 'var(--ferro-space-3, 12px)' }}
        >
          Only an owner can change the rewards value.
        </p>
      )}
    </Card>
  );
}
